<template>
  <IQOS :title="$t('preorder.packaging.title')" required>
    <div class="packaging-wrapper">
      <BaseTabSwitcher
        v-model="selectedMode"
        :tabs="[
          {
            value: 'preorder',
            text: 'TAKEAWAY',
          },
          {
            value: 'delivery',
            text: 'DELIVERY',
          },
        ]"
        v-if="
          smartPackagingEnabledFor.preorder && smartPackagingEnabledFor.delivery
        "
      />

      <div class="packaging-container">
        <div class="qrw-label-lg">
          {{ $t("packaging_attachment.default_title") }}
        </div>
        <BaseSelect v-model="edit_fields.defaults[selectedMode]" @blur="save">
          <option :value="null">
            {{ $t("packaging_attachment.no_default") }}
          </option>
          <option v-for="item in packagings" :key="item._id" :value="item._id">
            {{ item.name }}
          </option>
        </BaseSelect>
        <div
          class="qrw-label-xs v1-grey-7-color"
          v-if="selectedDefaultPackaging"
        >
          {{
            $t("packaging_attachment.price_hint", {
              price: selectedDefaultPackaging.price,
              currency: currency.symbol,
              tax: selectedDefaultTax ? selectedDefaultTax.percent : 0,
            })
          }}
        </div>
      </div>

      <template v-for="(pkg, index) in edit_fields.custom">
        <BaseDivider color="v1-grey-4" :key="`divider-${index}`" v-if="pkg.mode == selectedMode" />
        <div
          class="packaging-container"
          v-if="pkg.mode == selectedMode"
          :key="index"
        >
          <div class="gap-column-xs">
            <div class="qrw-label-lg w-100">
              {{ pkg.name || $t("add_custom_package.title") }}
            </div>

            <BaseButton
              color="red-4"
              variant="tertiary"
              size="sm"
              icon="trash"
              @click="removeCustomPackaging(index)"
            />
          </div>

          <BaseSelect v-model="pkg.packaging_id" @blur="save">
            <option :value="null">
              {{ $t("packaging_attachment.no_default") }}
            </option>
            <option
              v-for="item in packagings"
              :key="item._id"
              :value="item._id"
            >
              {{ item.name }}
            </option>
          </BaseSelect>
          <div
            class="qrw-label-xs v1-grey-7-color"
            v-if="!!findSelectedPackaging(pkg.packaging_id)"
          >
            {{
              $t("packaging_attachment.price_hint", {
                price: findSelectedPackaging(pkg.packaging_id).price,
                currency: currency.symbol,
                tax: findSelectedTax(
                  findSelectedPackaging(pkg.packaging_id).tax_id
                )
                  ? findSelectedTax(
                      findSelectedPackaging(pkg.packaging_id).tax_id
                    ).percent
                  : 0,
              })
            }}
          </div>
        </div>
        
        
      </template>

      <BaseDivider color="v1-grey-4" v-if="canAddCustomPackaging" />

      <BaseButton
        class="w-100"
        icon="plus"
        @click="adding_custom_package = true"
        variant="secondary"
        size="md"
        color="v1-grey-7"
        v-if="canAddCustomPackaging"
      >
        {{ $t("packaging_attachment.custom_btn") }}
      </BaseButton>
    </div>

    <AddCustomPackagingModal
      :dish="dish"
      :packagings="packagings"
      @add="onCustomPackageAdd"
      :dishConfigs="currentModeCustomConfigs"
      @close="adding_custom_package = false"
      v-if="adding_custom_package"
    />
  </IQOS>
</template>

<script>
import NomenculaturePackagingAPI from "@/api/nomenculature/packaging";

import AddCustomPackagingModal from "@/components/modals/AddCustomPackagingModal.vue";

import TaxesAPI from "@/api/taxes";

export default {
  data() {
    return {
      packagings: [],
      taxes: [],
      selectedMode: "preorder",

      adding_custom_package: false,

      edit_fields: {
        defaults: {
          preorder: null,
          delivery: null,
        },
        custom: [],
      },
    };
  },
  watch: {
    dish() {
      this.resetEditFields();
    },
  },
  methods: {
    fetchPackagings() {
      NomenculaturePackagingAPI.getPackagings(this.$store.state.cafe._id)
        .then((data) => {
          this.packagings = data.packagings;
        })
        .catch((err) => {});
    },
    resetEditFields() {
      this.edit_fields = {
        ...this.dish.packaging_settings,
      };
    },
    fetchTaxes() {
      TaxesAPI.getTaxes(this.$store.state.cafe._id)
        .then((data) => {
          this.taxes = data.taxes || [];
        })
        .catch((err) => {
          this.taxes = [];
        });
    },
    save() {
      this.$store.commit("menu/setProductData", {
        key: "packaging_settings",
        value: this.edit_fields,
      });
      this.$store.dispatch("menu/updateProduct");
    },
    onCustomPackageAdd(params) {
      this.edit_fields.custom.push({
        ...params,
        mode: this.selectedMode,
      });

      this.adding_custom_package = false;
      this.save();
    },
    findSelectedPackaging(id) {
      return this.packagings.find((pk) => pk._id == id);
    },
    findSelectedTax(tax_id) {
      return this.taxes.find((t) => t._id == tax_id);
    },
    removeCustomPackaging(index) {
      this.edit_fields.custom.splice(index, 1);
      this.save();
    },
  },
  mounted() {
    this.fetchPackagings();
    this.fetchTaxes();

    if (this.smartPackagingEnabledFor.preorder) {
      this.selectedMode = "preorder";
    } else if (this.smartPackagingEnabledFor.delivery) {
      this.selectedMode = "delivery";
    }

    this.resetEditFields();
  },
  computed: {
    uiShowModeSwitcher() {
      return (
        this.smartPackagingEnabledFor.preorder &&
        this.smartPackagingEnabledFor.delivery
      );
    },
    smartPackagingEnabledFor() {
      return {
        preorder: this.$store.state.cafe.preorder.packaging_type === "weighted",
        delivery: this.$store.state.cafe.delivery.packaging_type === "weighted",
      };
    },
    dish() {
      return this.$store.state.menu.product;
    },
    selectedDefaultPackaging() {
      return this.packagings.find(
        (p) => p._id == this.edit_fields.defaults[this.selectedMode]
      );
    },
    selectedDefaultTax() {
      if (!this.selectedDefaultPackaging) return null;
      return this.taxes.find(
        (t) => t._id == this.selectedDefaultPackaging.tax_id
      );
    },
    currency() {
      return this.$store.state.currency;
    },
    canAddCustomPackaging() {
      return this.dish.price_groups.length;
    },
    currentModeCustomConfigs() {
      return this.edit_fields.custom
        .filter((c) => c.mode == this.selectedMode)
        .map((c) => c.modificator_ids);
    },
  },
  components: {
    AddCustomPackagingModal,
  },
};
</script>

<style scoped>
.packaging-container {
  display: flex;
  flex-direction: column;
  row-gap: 8px;
}

.packaging-wrapper {
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
}
</style>    